import { useState, useEffect, useRef } from "react";
import { ScanBarcode, Camera, Search, Package, Plus, Minus } from "lucide-react";
import { productApi, transactionApi } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export default function Scanner() {
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [scannedProduct, setScannedProduct] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [quantity, setQuantity] = useState("1");
  const [transactionType, setTransactionType] = useState("out");
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
      setScanning(true);
      toast.info("Camera ready. Position barcode in view.");
    } catch (error) {
      toast.error("Could not access camera. Please allow camera permissions.");
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setScanning(false);
  };

  const handleManualSearch = async (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    
    try {
      const response = await productApi.getBySku(manualCode.toUpperCase());
      setScannedProduct(response.data);
      setActionDialogOpen(true);
      setManualCode("");
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(`Product not found: ${manualCode}`);
      } else {
        toast.error("Search failed");
      }
    }
  };

  const handleTransaction = async () => {
    if (!scannedProduct || !quantity) return;
    
    try {
      await transactionApi.create({
        product_id: scannedProduct.id,
        transaction_type: transactionType,
        quantity: parseInt(quantity),
        notes: "Quick scan transaction",
      });
      
      toast.success(
        transactionType === "in" 
          ? `Added ${quantity} units to ${scannedProduct.name}` 
          : `Removed ${quantity} units from ${scannedProduct.name}`
      );
      
      setActionDialogOpen(false);
      setScannedProduct(null);
      setQuantity("1");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Transaction failed");
    }
  };

  return (
    <div className="animate-fadeIn" data-testid="scanner-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">Barcode Scanner</h1>
        <p className="text-slate-400">Scan products or search by SKU</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Manual Search */}
        <div className="bg-[#151923] border border-slate-800 rounded-sm p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Search className="w-5 h-5 text-blue-400" />
            Search by SKU
          </h2>
          <form onSubmit={handleManualSearch} className="space-y-4">
            <div>
              <label className="form-label">Enter SKU or Barcode</label>
              <Input
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                className="bg-[#0B0E14] border-slate-700 text-lg font-mono"
                placeholder="e.g., SKU001"
                data-testid="sku-search-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-500"
              data-testid="sku-search-btn"
            >
              <Search className="w-4 h-4 mr-2" />
              Search Product
            </Button>
          </form>
        </div>

        {/* Camera Scanner */}
        <div className="bg-[#151923] border border-slate-800 rounded-sm p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Camera className="w-5 h-5 text-blue-400" />
            Camera Scanner
          </h2>
          
          <div className="relative aspect-video bg-[#0B0E14] rounded-sm overflow-hidden mb-4">
            {scanning ? (
              <>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                />
                {/* Scan overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-64 h-40 border-2 border-blue-500 rounded relative">
                    <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-blue-400" />
                    <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-blue-400" />
                    <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-blue-400" />
                    <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-blue-400" />
                    {/* Scan line animation */}
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-red-500 animate-[scan_2s_ease-in-out_infinite]" />
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-slate-500">
                <ScanBarcode className="w-16 h-16 mb-4" />
                <p>Camera not active</p>
              </div>
            )}
          </div>

          <Button
            onClick={scanning ? stopCamera : startCamera}
            className={`w-full ${scanning ? "bg-red-600 hover:bg-red-500" : "bg-blue-600 hover:bg-blue-500"}`}
            data-testid="camera-toggle-btn"
          >
            {scanning ? (
              <>Stop Scanner</>
            ) : (
              <>
                <Camera className="w-4 h-4 mr-2" />
                Start Camera
              </>
            )}
          </Button>

          <p className="text-xs text-slate-500 mt-3 text-center">
            Note: For full barcode scanning, use a dedicated barcode scanner that inputs as keyboard.
            Point your barcode to the SKU search field above.
          </p>
        </div>
      </div>

      {/* Quick Tips */}
      <div className="mt-8 bg-[#151923] border border-slate-800 rounded-sm p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Quick Tips</h2>
        <ul className="space-y-2 text-slate-400 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-blue-400">1.</span>
            Use a USB/Bluetooth barcode scanner for fast inventory updates
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400">2.</span>
            The scanner will automatically input the code into the SKU search field
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400">3.</span>
            After scanning, quickly adjust stock levels with the popup dialog
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400">4.</span>
            Use the camera scanner for mobile devices (position barcode in the frame)
          </li>
        </ul>
      </div>

      {/* Product Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent className="bg-[#151923] border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Quick Stock Update</DialogTitle>
          </DialogHeader>
          
          {scannedProduct && (
            <div className="space-y-4">
              {/* Product Info */}
              <div className="bg-[#0B0E14] rounded-sm p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-500/10 rounded">
                    <Package className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">{scannedProduct.name}</p>
                    <p className="text-sm text-slate-400 font-mono">{scannedProduct.sku}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">In Stock</p>
                    <p className="text-lg font-mono text-white">{scannedProduct.quantity}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Unit Price</p>
                    <p className="text-lg font-mono text-white">${scannedProduct.unit_price.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Min Stock</p>
                    <p className="text-lg font-mono text-white">{scannedProduct.min_stock}</p>
                  </div>
                </div>
              </div>

              {/* Action Selection */}
              <div>
                <label className="form-label">Action</label>
                <Select value={transactionType} onValueChange={setTransactionType}>
                  <SelectTrigger className="bg-[#0B0E14] border-slate-700" data-testid="scan-action-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#151923] border-slate-700">
                    <SelectItem value="in">
                      <span className="flex items-center gap-2">
                        <Plus className="w-4 h-4 text-emerald-400" />
                        Add Stock (In)
                      </span>
                    </SelectItem>
                    <SelectItem value="out">
                      <span className="flex items-center gap-2">
                        <Minus className="w-4 h-4 text-red-400" />
                        Remove Stock (Out)
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Quantity */}
              <div>
                <label className="form-label">Quantity</label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, parseInt(quantity || 1) - 1).toString())}
                    className="bg-slate-800 border-slate-700 hover:bg-slate-700"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <Input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className="bg-[#0B0E14] border-slate-700 text-center text-lg font-mono"
                    data-testid="scan-quantity-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity((parseInt(quantity || 1) + 1).toString())}
                    className="bg-slate-800 border-slate-700 hover:bg-slate-700"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="ghost"
                  onClick={() => setActionDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleTransaction}
                  className={transactionType === "in" ? "bg-emerald-600 hover:bg-emerald-500" : "bg-red-600 hover:bg-red-500"}
                  data-testid="scan-confirm-btn"
                >
                  {transactionType === "in" ? "Add Stock" : "Remove Stock"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes scan {
          0%, 100% { top: 0; }
          50% { top: calc(100% - 2px); }
        }
      `}</style>
    </div>
  );
}
